import { useContext, useEffect, useState } from "react";
import { Switch, Route, useHistory } from "react-router-dom";

import { LoanFormContext, FormState } from "../LoanForm";
import TypeOfPropertyForm, { TypeOfProperty } from "../TypeOfProperty";
import PropertyUsage, { TypeOfUsageProperty } from "../PropertyUsage";
import CreditHistory from "../CreditHistory";
import PropertyLocation from "../PropertyLocation";
import FoundHome from "../FoundHome";
import WorkingWithAgent from "../WorkingWithAgent";
import PlanToBuy from "../PlanToBuy";
import EstimatedPurchase from "../EstimatedPurchase";
import DownPaymentPercent from "../DownPaymentPercent";
import FirstTimeBuyer from "../FirstTimeBuyer";
import CoBorrow from "../CoBorrow";
import CombinedAnnualIncome from "../CombinedAnnualIncome";
import CurrentlyEmployeed from "../CurrentlyEmployeed";
import CreditScore from "../CreditScore";
import Dob from "../Dob";
import Bankruptcy from "../Bankruptcy";
import Foreclosure from "../Foreclosure";
import Address from "../Address";
import NameDetails from "../NameDetails";
import EmailDetails from "../Email";
import ContactNumber from "../ContactNumber";

import { CircularProgressbar } from "react-circular-progressbar";

const PurchaseForm: React.FunctionComponent<{ onSubmit?: () => void }> =
  (props: { onSubmit?: () => void }) => {
    const history = useHistory();
    const { setFormState, ...loanFormContext } = useContext(LoanFormContext);
    const [percent, setPercent] = useState<number>(0);

    useEffect(() => {
      setFormState((state: FormState) => ({
        ...state,
        steps: {
          current: 1,
          total: 20,
        },
      }));
    }, []);

    useEffect(() => {
      const l = history.listen((location, action) => {
        if (action === "POP") {
          setFormState((state: FormState) => {
            return {
              ...state,
              steps: {
                current: (state.steps ? state.steps.current : 0) - 1,
                total: state.steps ? state.steps.total : 0,
              },
            };
          });
        }
      });

      return () => {
        l();
      };
    }, []);

    useEffect(() => {
      const currentStep = loanFormContext.steps
        ? loanFormContext.steps?.current
        : 1;
      const totalSteps = loanFormContext.steps
        ? loanFormContext.steps?.total
        : 1;
      setPercent(Math.ceil((currentStep / totalSteps) * 100));
      console.log(currentStep, totalSteps);
    }, [loanFormContext.steps]);

    return (
      <div>
        <div className="progress-bar-wrapper">
          <CircularProgressbar value={percent} text={`${percent}%`} />
        </div>
        <Switch>
          <Route exact path="/purchase/type-of-property">
            <TypeOfPropertyForm
              onSubmit={(value: TypeOfProperty) => {
                setFormState((state: FormState) => {
                  return {
                    ...state,
                    steps: {
                      total: state.steps ? state.steps.total : 0,
                      current: (state.steps ? state.steps.current : 0) + 1,
                    },
                    typeOfProperty: { value },
                  };
                });
                history.push("/purchase/property-usage");
              }}
            />
          </Route>

          <Route path="/purchase/property-usage">
            <PropertyUsage
              onSubmit={(value: TypeOfUsageProperty) => {
                setFormState((state: FormState) => {
                  return {
                    ...state,
                    steps: {
                      total: state.steps ? state.steps.total : 0,
                      current: (state.steps ? state.steps.current : 0) + 1,
                    },
                    propertyUsage: { value },
                  };
                });
                history.push("/purchase/found-home");
              }}
            />
          </Route>

          <Route exact path="/purchase/found-home">
            <PropertyLocation
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  propertyLocation: value,
                }));
                history.push("/purchase/plan-to-buy");
              }}
            />
          </Route>

          <Route exact path="/purchase/plan-to-buy">
            <FoundHome
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  foundHome: { value },
                }));
                history.push("/purchase/working-with-agent");
              }}
            />
          </Route>

          <Route exact path="/purchase/working-with-agent">
            <WorkingWithAgent
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  workingWithAgent: { value },
                }));
                history.push("/purchase/estimated-purchase");
              }}
            />
          </Route>
          <Route exact path="/purchase/estimated-purchase">
            <PlanToBuy
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  planToBuy: { value },
                }));
                history.push("/purchase/estimate-purchase");
              }}
            />
          </Route>

          <Route exact path="/purchase/estimate-purchase">
            <EstimatedPurchase
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  estimatedPruchase: {
                    value: { min: value[0], max: value[1] },
                  },
                }));
                history.push("/purchase/down-payment");
              }}
            />
          </Route>

          <Route exact path="/purchase/down-payment">
            <DownPaymentPercent
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  downPayment: {
                    value: { value: value[0], upperBound: value[1] },
                  },
                }));
                history.push("/purchase/first-time-buyer");
              }}
            />
          </Route>

          <Route exact path="/purchase/first-time-buyer">
            <FirstTimeBuyer
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  firstTimeBuyer: {
                    value,
                  },
                }));
                history.push("/purchase/co-borrow");
              }}
            />
          </Route>

          <Route exact path="/purchase/co-borrow">
            <CoBorrow
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  coBorrow: {
                    value,
                  },
                }));
                history.push("/purchase/combined-income");
              }}
            />
          </Route>

          <Route exact path="/purchase/combined-income">
            <CombinedAnnualIncome
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  combinedAnnualIncome: {
                    value,
                  },
                }));
                history.push("/purchase/employment-status");
              }}
            />
          </Route>

          <Route exact path="/purchase/employment-status">
            <CurrentlyEmployeed
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  employment: {
                    value,
                  },
                  activeRoute: "CreditScore",
                }));
                history.push("/purchase/credit-score");
              }}
            />
          </Route>

          <Route exact path="/purchase/credit-score">
            <CreditScore
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  creditScore: {
                    value,
                  },
                }));
                history.push("/purchase/dob");
              }}
            />
          </Route>

          <Route exact path="/purchase/dob">
            <Dob
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  dob: {
                    value,
                  },
                }));
                history.push("/purchase/credit-history");
              }}
            />
          </Route>

          <Route exact path="/purchase/credit-history">
            <CreditHistory
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  creditHistory: {
                    type: value,
                  },
                }));
                if (value === "both" || value === "bankruptcy") {
                  history.push("/purchase/bankrupt-history");
                } else if (value === "foreclosure") {
                  history.push("/purchase/foreclosure-history");
                } else {
                  history.push("/purchase/address");
                }
              }}
            />
          </Route>

          <Route exact path="/purchase/bankrupt-history">
            <Bankruptcy
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  creditHistory: {
                    type: state.creditHistory
                      ? state.creditHistory.type
                      : "bankruptcy",
                    bankruptcy: { yearsAgo: value },
                  },
                }));
                if (
                  loanFormContext.creditHistory?.type === "foreclosure" ||
                  loanFormContext.creditHistory?.type === "both"
                ) {
                  history.push("/purchase/foreclosure-history");
                } else {
                  history.push("/purchase/address");
                }
              }}
            />
          </Route>

          <Route exact path="/purchase/foreclosure-history">
            <Foreclosure
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  creditHistory: {
                    type: state.creditHistory
                      ? state.creditHistory.type
                      : "foreclosure",
                    foreclosure: { yearsAgo: value },
                  },
                }));
                history.push("/purchase/address");
              }}
            />
          </Route>

          <Route exact path="/purchase/address">
            <Address
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  address: value,
                }));
                history.push("/purchase/name-details");
              }}
            />
          </Route>

          <Route exact path="/purchase/name-details">
            <NameDetails
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  name: value,
                }));
                history.push("/purchase/email");
              }}
            />
          </Route>

          <Route exact path="/purchase/email">
            <EmailDetails
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  email: value,
                }));
                history.push("/purchase/contact-number");
              }}
            />
          </Route>

          <Route exact path="/purchase/contact-number">
            <ContactNumber
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  number: value,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current:
                      (state.steps && percent < 100 ? state.steps.current : 0) +
                      1,
                  },
                }));
                // history.push("/purchase/employment-status");
                if (props.onSubmit) props.onSubmit();
              }}
            />
          </Route>
        </Switch>
      </div>
    );
  };

export default PurchaseForm;
